/**
 * Reading a trade out of whatever somebody pasted.
 *
 * Trades are declared in Discord in prose, and the prose has settled into a handful of shapes
 * over eleven years:
 *
 *   Rajan gets: Justin Jefferson, Terry McLaurin, 2026 14th, 2026 15th
 *   John gets: Keon Coleman, Jayden Higgins, 2026 9th, 2026 11th
 *
 *   @Tim sends CMC + a 2026 3rd to @Luke for Bijan Robinson and $10 FAAB
 *
 *   TRADE — Luke receives 2025 6.10 and $5 / Vishal receives 2025 6.5
 *
 * This turns any of those into a structured trade. It is deliberately NOT clever: it will not
 * guess a manager it cannot name, it will not silently drop a phrase it cannot classify, and it
 * reports everything it was unsure about. The output goes to a review form, never straight to the
 * ledger — a parser that writes to the record without a human looking is a parser that will one
 * day give away the wrong first-round pick.
 *
 * The same function serves the paste box and the screenshot: OCR produces text, and text comes
 * here. That way there is one set of rules to trust rather than two.
 */

import managersData from '../data/managers.json';
import { MANAGERS } from './identity.js';
import { faab } from './faab.js';
import { cleanPlayerName } from './names.js';

/* -- who -------------------------------------------------------------------------------------- */

const MANAGER_ALIASES: Array<{ id: string; alias: string }> = [];
for (const m of MANAGERS) {
  const id = m.coManagerOf ?? m.id;
  MANAGER_ALIASES.push({ id, alias: m.id.toLowerCase() });
  for (const part of m.name.split(/\s+/)) MANAGER_ALIASES.push({ id, alias: part.toLowerCase() });
  MANAGER_ALIASES.push({ id, alias: m.name.toLowerCase() });
}
// Team names too: half the league refers to each other by team in #trade-declarations.
for (const [team, gm] of Object.entries(managersData as Record<string, string>)) {
  MANAGER_ALIASES.push({ id: gm.toUpperCase(), alias: team.toLowerCase() });
}
// Longest first, so "john wardach" wins over "john".
MANAGER_ALIASES.sort((a, b) => b.alias.length - a.alias.length);

/** The manager a fragment names, or null. Never guesses from an initial. */
export function managerIn(text: string): string | null {
  const hay = ` ${text.toLowerCase().replace(/[@*_`]/g, ' ').replace(/\s+/g, ' ')} `;
  for (const { id, alias } of MANAGER_ALIASES) {
    if (alias.length < 3) continue;
    if (hay.includes(` ${alias} `) || hay.includes(` ${alias}'s `) || hay.includes(` ${alias}:`)) {
      return id;
    }
  }
  return null;
}

/* -- what ------------------------------------------------------------------------------------- */

export type AssetKind = 'player' | 'rights' | 'pick' | 'faab' | 'unknown';

export interface ParsedAsset {
  kind: AssetKind;
  /** Cleaned display text. For a pick this is the canonical "2026 9.04" or "2026 9th". */
  text: string;
  /** What the person actually wrote, kept so a bad parse is visible. */
  raw: string;
  season?: number;
  round?: number;
  slot?: number;
  farm?: boolean;
  amount?: number;
}

const ROUND_WORDS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8,
  ninth: 9, tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14,
  fifteenth: 15, sixteenth: 16,
};

const POS = /^(QB|RB|WR|TE|K|DEF|D\/ST|IDP)\b/i;

/** One phrase — "2026 14th", "$5 FAAB", "rights to WR AJ Green", "Justin Jefferson". */
export function classifyAsset(raw: string, defaultSeason: number): ParsedAsset | null {
  const text = raw.replace(/^[\s,;+•\-–—]+|[\s,;.+]+$/g, '').trim();
  if (!text || /^(and|plus|for|the|a|an)$/i.test(text)) return null;

  // FAAB, in any of the four ways people write it.
  const money = text.match(/\$\s*(\d{1,3})|(\d{1,3})\s*(?:faab|fab|\$)/i);
  if (money && /faab|fab|\$/i.test(text)) {
    const amount = Number(money[1] ?? money[2]);
    return { kind: 'faab', text: `${faab(amount)} FAAB`, raw: text, amount };
  }

  // Keeper rights to a farm player move separately from the player himself.
  const rights = text.match(/^(?:the\s+)?(?:keeper\s+)?rights?\s*(?:to|for|:)?\s*(.+)$/i)
    ?? text.match(/^(.+?)\s*\((?:keeper\s+)?rights\)$/i);
  if (rights) {
    return { kind: 'rights', text: cleanAsset(rights[1]!), raw: text };
  }

  const farm = /\bfarm\b/i.test(text);
  const year = text.match(/\b(20\d\d)\b/);
  const season = year ? Number(year[1]) : defaultSeason;

  // "9.04", "R9.4", "2026 9.x"
  const dotted = text.match(/\b(\d{1,2})\.(x|\d{1,2})\b/i);
  if (dotted && !farm) {
    const slot = dotted[2]!.toLowerCase() === 'x' ? undefined : Number(dotted[2]);
    const round = Number(dotted[1]);
    return {
      kind: 'pick', raw: text, season, round, farm: false,
      ...(slot === undefined ? {} : { slot }),
      text: `${season} ${round}.${slot === undefined ? 'x' : String(slot).padStart(2, '0')}`,
    };
  }

  // "14th", "14th rounder", "round 14", "third-round pick", "2026 FARM (#8)"
  const ord = text.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)\b/i)
    ?? text.match(/\bround\s*(\d{1,2})\b/i)
    ?? text.match(/\br(\d{1,2})\b/i);
  const word = Object.keys(ROUND_WORDS).find((w) => new RegExp(`\\b${w}\\b`, 'i').test(text));
  const hash = text.match(/#\s*(\d{1,3})/);
  const round = ord ? Number(ord[1]) : word ? ROUND_WORDS[word] : undefined;

  if (farm) {
    return {
      kind: 'pick', raw: text, season, farm: true,
      ...(round === undefined ? {} : { round }),
      ...(hash ? { slot: Number(hash[1]) } : {}),
      text: `${season} FARM${hash ? ` (#${hash[1]})` : ''}`,
    };
  }

  if (round !== undefined && (/pick|round|rd\b|\d(?:st|nd|rd|th)/i.test(text))) {
    return {
      kind: 'pick', raw: text, season, round, farm: false,
      text: `${season} ${round}${ordSuffix(round)}`,
    };
  }

  // Anything left with letters in it is a player.
  if (/[a-z]{2}/i.test(text)) return { kind: 'player', text: cleanAsset(text), raw: text };
  return { kind: 'unknown', text, raw: text };
}

const ordSuffix = (n: number) => (n % 10 === 1 && n !== 11 ? 'st'
  : n % 10 === 2 && n !== 12 ? 'nd'
    : n % 10 === 3 && n !== 13 ? 'rd' : 'th');

/**
 * Tidy a player fragment without destroying it. Positions written in front of the name are
 * kept, because the Trade Log has always written them that way and they disambiguate.
 */
function cleanAsset(s: string): string {
  const t = s.replace(/^[\s,;+]+|[\s,;.]+$/g, '').replace(/\s+/g, ' ').trim();
  const pos = t.match(POS);
  if (pos) {
    const rest = cleanPlayerName(t.slice(pos[0].length)).name;
    return `${pos[0].toUpperCase()} ${rest}`.trim();
  }
  return cleanPlayerName(t).name;
}

/* -- the whole thing --------------------------------------------------------------------------- */

export interface ParsedSide {
  gm: string | null;
  assets: ParsedAsset[];
}

export interface ParsedTrade {
  sides: [ParsedSide, ParsedSide];
  date: string | null;
  season: number;
  /** Everything the parser wants a human to look at before this is saved. */
  warnings: string[];
  /** Lines it could not place at all, kept verbatim so nothing is silently lost. */
  leftovers: string[];
}

const GETS = /\b(gets?|receives?|acquires?|lands?|gains?)\b\s*:?/i;
const SENDS = /\b(sends?|trades?|gives?|ships?)\b/i;

const splitAssets = (s: string) => s
  .split(/\s*(?:,|;|\+|\band\b|\/|\n)\s*/i)
  .map((x) => x.trim())
  .filter(Boolean);

/**
 * Parse a Discord declaration.
 *
 * Two shapes are recognised. The common one is two "X gets: …" lines. The other is a single
 * sentence — "A sends X to B for Y" — which is how the quick ones get written.
 */
export function parseTrade(input: string, defaultSeason: number): ParsedTrade {
  const warnings: string[] = [];
  const leftovers: string[] = [];
  const lines = input.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  const date = findDate(input);
  const sides: ParsedSide[] = [];

  // Shape one: a line per side.
  for (const line of lines) {
    if (/^(trade|trade declaration|declaring|proposed|accepted)\b[:\s—-]*$/i.test(line)) continue;
    const m = line.match(GETS);
    if (!m) continue;
    const who = managerIn(line.slice(0, m.index! + m[0].length));
    const rest = line.slice(m.index! + m[0].length);
    const assets = splitAssets(rest)
      .map((a) => classifyAsset(a, defaultSeason))
      .filter((a): a is ParsedAsset => a !== null);
    if (!who) warnings.push(`Could not tell who this is: "${truncate(line)}"`);
    if (assets.length === 0) warnings.push(`No players or picks found in "${truncate(line)}"`);
    sides.push({ gm: who, assets });
  }

  // Shape two: one sentence, "A sends X to B for Y".
  if (sides.length < 2) {
    const one = input.replace(/\s+/g, ' ').trim();
    const sendMatch = one.match(SENDS);
    const toMatch = one.match(/\bto\b/i);
    const forMatch = one.match(/\bfor\b/i);
    if (sendMatch && toMatch && forMatch && toMatch.index! > sendMatch.index!) {
      const a = managerIn(one.slice(0, sendMatch.index!));
      const b = managerIn(one.slice(toMatch.index!, forMatch.index!));
      const aSends = one.slice(sendMatch.index! + sendMatch[0].length, toMatch.index!);
      const bSends = one.slice(forMatch.index! + forMatch[0].length);
      const parse = (s: string) => splitAssets(s)
        .map((x) => classifyAsset(x, defaultSeason))
        .filter((x): x is ParsedAsset => x !== null);
      // "A sends X to B" means B GETS X — the sides below are always what each man received.
      sides.length = 0;
      sides.push({ gm: b, assets: parse(aSends) }, { gm: a, assets: parse(bSends) });
      if (!a || !b) warnings.push('Could not name both managers with confidence.');
    }
  }

  while (sides.length < 2) sides.push({ gm: null, assets: [] });
  if (sides.length > 2) {
    for (const extra of sides.splice(2)) {
      leftovers.push(`${extra.gm ?? 'someone'}: ${extra.assets.map((x) => x.text).join(', ')}`);
    }
    warnings.push('More than two sides — this app records two-team trades; check the extras.');
  }

  const [a, b] = sides as [ParsedSide, ParsedSide];
  if (a.gm && a.gm === b.gm) warnings.push('Both sides came out as the same manager.');
  if (a.assets.length === 0 || b.assets.length === 0) {
    warnings.push('One side is empty — a trade that only goes one way is usually a mis-parse.');
  }
  for (const s of [a, b]) {
    for (const x of s.assets) {
      if (x.kind === 'unknown') warnings.push(`Not sure what "${x.raw}" is.`);
    }
  }

  const seasons = [a, b].flatMap((s) => s.assets.map((x) => x.season))
    .filter((x): x is number => typeof x === 'number');

  return {
    sides: [a, b],
    date,
    season: seasons.length ? Math.min(...seasons) : defaultSeason,
    warnings,
    leftovers,
  };
}

const truncate = (s: string) => (s.length > 60 ? `${s.slice(0, 57)}…` : s);

/** A date anywhere in the text, in the three formats Discord shows. */
export function findDate(text: string): string | null {
  const iso = text.match(/\b(20\d\d)-(\d{2})-(\d{2})\b/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const slash = text.match(/\b(\d{1,2})\/(\d{1,2})\/(20\d\d|\d{2})\b/);
  if (slash) {
    const y = slash[3]!.length === 2 ? `20${slash[3]}` : slash[3]!;
    return `${y}-${slash[1]!.padStart(2, '0')}-${slash[2]!.padStart(2, '0')}`;
  }

  const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
    'september', 'october', 'november', 'december'];
  const named = text.toLowerCase().match(
    /\b([a-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(20\d\d)?\b/,
  );
  if (named) {
    const i = MONTHS.findIndex((m) => m.startsWith(named[1]!.slice(0, 3)) && named[1]!.length >= 3);
    if (i >= 0) {
      const y = named[3] ?? String(new Date().getFullYear());
      return `${y}-${String(i + 1).padStart(2, '0')}-${named[2]!.padStart(2, '0')}`;
    }
  }
  return null;
}
